import React, { useEffect, useState, useCallback, useRef } from "react"
import { createPortal } from "react-dom"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "../../../redux/store"
import {
  deleteGuest,
  getContactsOnEvent,
  loadMoreContacts,
  setCurrentPage,
  setLoading,
} from "../../../redux/slices/contacts/contactsSlices"
import { sendReminder, sendConfirmedQrReminders } from "../../../redux/slices/events/eventsSlice"
import { setBalance } from "../../../redux/slices/auth/authSlice"
import { IEvent } from "../../../components/Events/types"
import { IContact } from "../../../components/Contacts/types"
import { useInfiniteScroll } from "../hooks/useInfiniteScroll"
import { getUnifiedStatus } from "./GuestList"
import styles from "../EventDashboardV2.module.scss"

import {
  type InvitationStatusFilter,
  type ReminderStatusFilter,
  INVITATION_FILTERS,
  REMINDER_FILTERS,
  matchesInvitationFilter,
  matchesStatusWithFailureType,
} from "../constants/filters"
import { fetchAllEventContacts, FETCH_ALL_PAGE_SIZE } from "../utils/fetchAllEventContacts"
export type { InvitationStatusFilter, ReminderStatusFilter }

interface RemindersGuestListProps {
  event: IEvent
  invitationFilter: InvitationStatusFilter
  reminderFilter: ReminderStatusFilter
  onInvitationFilterChange: (filter: InvitationStatusFilter) => void
  onReminderFilterChange: (filter: ReminderStatusFilter) => void
  editPopupOpen: (guest: any) => void
  filter: any
  notify: (text: string, isError: boolean) => void
  reminderType?: number
}

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)


const EditSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)


const WhatsAppSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
)

const MailSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const TrashSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
)

const SpinnerIcon = () => (
  <svg className={styles.spinIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
)

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="14"
    height="14"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)

const getStatusDotColor = (status: number): string => {
  switch (status) {
    case 1:
      return "Green"
    case 2:
    case 4:
    case 6:
      return "Red"
    case 3:
      return "Orange"
    default:
      return "Gray"
  }
}

const getStatusFieldForType = (reminderType: number): string => {
  switch (reminderType) {
    case 1:
      return "rsvp_reminder_status"
    case 2:
      return "qr_reminder_status"
    default:
      return "reminder_status"
  }
}

const getFailureTypeFieldForType = (reminderType: number): string => {
  switch (reminderType) {
    case 1:
      return "rsvp_reminder_failure_type"
    case 2:
      return "qr_reminder_failure_type"
    default:
      return "reminder_failure_type"
  }
}

const isQrReminderEligible = (guest: any): boolean =>
  guest.status === 1 || guest.status === 4

const getSentAtFieldForType = (reminderType: number): string => {
  switch (reminderType) {
    case 1:
      return "rsvp_reminder_sent_at"
    case 2:
      return "qr_reminder_sent_at"
    default:
      return "reminder_sent_at"
  }
}

const getReminderStatus = (
  guest: any,
  reminderType: number = 0,
  dateLocale: string,
): { text: string; variant: string; hasRetry?: boolean; date?: string } => {
  const statusField = getStatusFieldForType(reminderType)
  const sentAtField = getSentAtFieldForType(reminderType)
  const status = guest[statusField]

  switch (status) {
    case "sent": {
      const date = guest[sentAtField]
        ? new Date(guest[sentAtField]).toLocaleDateString(dateLocale, {
            month: "short",
            day: "numeric",
          })
        : ""
      return { text: "Sent", variant: "Sent", date }
    }
    case "failed":
      return { text: "Failed", variant: "Issue", hasRetry: true }
    case "not_sent":
    default:
      return { text: "Not sent", variant: "NotSent" }
  }
}

interface DeleteButtonProps {
  guestId: number
  guestName: string
  eventId: string
  filter: any
  notify: (text: string, isError: boolean) => void
}

const DeleteButton: React.FC<DeleteButtonProps> = ({ guestId, guestName, eventId, filter, notify }) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (!confirming) return
    const onClickOutside = (e: MouseEvent) => {
      if (
        popRef.current && !popRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setConfirming(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [confirming])

  const openConfirm = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({
        top: rect.bottom + 6 + window.scrollY,
        left: rect.right - 240 + window.scrollX,
      })
    }
    setConfirming(true)
  }

  const handleDelete = () => {
    setDeleting(true)
    dispatch(deleteGuest({ eventId, guestId: [{ contact_id: guestId }] }))
      .then((result: any) => {
        if (result.error) {
          notify(t("eventDashboardV2.messages.failedRemoveGuest"), true)
        } else {
          dispatch(setLoading())
          dispatch(getContactsOnEvent(filter))
          notify(t("eventDashboardV2.messages.guestRemoved"), false)
        }
        setDeleting(false)
        setConfirming(false)
      })
      .catch(() => {
        notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
        setDeleting(false)
        setConfirming(false)
      })
  }

  return (
    <>
      <button
        ref={btnRef}
        className={`${styles.actionIconBtn} ${styles.actionIconBtnDanger}`}
        title={t("eventDashboardV2.actions.remove")}
        onClick={openConfirm}
      >
        <TrashSmallIcon />
      </button>
      {confirming && createPortal(
        <div
          ref={popRef}
          className={styles.deleteConfirm}
          style={{ top: pos.top, left: pos.left }}
        >
          <p className={styles.deleteConfirmText}>
            {t("eventDashboardV2.messages.confirmRemoveGuest", { name: guestName })}
          </p>
          <div className={styles.deleteConfirmActions}>
            <button
              type="button"
              className={styles.deleteConfirmCancel}
              onClick={() => setConfirming(false)}
              disabled={deleting}
            >
              {t("eventDashboardV2.actions.cancel")}
            </button>
            <button
              type="button"
              className={styles.deleteConfirmBtn}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? t("eventDashboardV2.messages.removing") : t("eventDashboardV2.actions.remove")}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

const RemindersGuestList: React.FC<RemindersGuestListProps> = ({
  event,
  invitationFilter,
  reminderFilter,
  onInvitationFilterChange,
  onReminderFilterChange,
  editPopupOpen,
  filter: parentFilter,
  notify,
  reminderType = 0,
}) => {
  const { event_id } = useParams()
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const { data_uploaded, current_page, total_pages, limit, isLoading } =
    useAppSelector((state) => state.contacts)

  const [searchValue, setSearchValue] = useState("")
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [sendPhase, setSendPhase] = useState<"idle" | "confirm" | "sending" | "success">("idle")
  const [sendResult, setSendResult] = useState({ sent: 0, skipped: 0 })
  const [eventDateText, setEventDateText] = useState("")
  const [eventDateError, setEventDateError] = useState("")
  const [selectedGuestMap, setSelectedGuestMap] = useState<Map<number, any>>(new Map())
  const [bulkGuests, setBulkGuests] = useState<any[] | null>(null)
  const [isPreparingSend, setIsPreparingSend] = useState(false)
  const [isSelectingAll, setIsSelectingAll] = useState(false)
  const dateInputRef = useRef<HTMLInputElement>(null)

  const isQrReminder = reminderType === 2

  const activeInv = INVITATION_FILTERS.find((f) => f.id === invitationFilter) ?? INVITATION_FILTERS[0]
  const activeRem = REMINDER_FILTERS.find((f) => f.id === reminderFilter) ?? REMINDER_FILTERS[0]
  const qrStatusNumber = isQrReminder && activeInv.id === "all" ? "1,4" : activeInv.statusNumber

  const filterParams = useCallback(() => ({
    sort: "",
    order: "",
    searchValue,
    id: Number(event_id),
    statusNumber: qrStatusNumber,
    waStatusNumber: activeInv.waStatusNumber,
    reminderNumber: "",
  }), [searchValue, event_id, qrStatusNumber, activeInv.waStatusNumber])

  const fetchGuests = useCallback(() => {
    dispatch(setCurrentPage(1))
    dispatch(getContactsOnEvent(filterParams()))
  }, [dispatch, filterParams])

  useEffect(() => {
    fetchGuests()
  }, [fetchGuests])

  const handleLoadMore = useCallback(async (page: number) => {
    await dispatch(loadMoreContacts({ page, params: filterParams() }))
  }, [dispatch, filterParams])

  const { sentinelRef, isFetchingMore, hasMore, reset } = useInfiniteScroll({
    totalPages: total_pages,
    onLoadMore: handleLoadMore,
  })

  const handleInvFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onInvitationFilterChange(e.target.value as InvitationStatusFilter)
    reset()
  }

  const handleRemFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onReminderFilterChange(e.target.value as ReminderStatusFilter)
    reset()
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
    reset()
  }

  const getContactDisplay = (guest: IContact) => {
    const phone = guest.phone ? guest.phone.split("|")[0] : ""
    const hasExtra = guest.phone && guest.phone.includes("|")
    return { phone, email: guest.email, hasExtra }
  }

  const matchesInvFilter = (guest: any): boolean =>
    matchesInvitationFilter(guest, activeInv)

  const statusField = getStatusFieldForType(reminderType)

  const failureTypeField = getFailureTypeFieldForType(reminderType)

  const matchesRemFilter = (guest: any): boolean =>
    matchesStatusWithFailureType(
      guest,
      statusField,
      failureTypeField,
      activeRem.id,
    )

  const matchesQrEligibility = (guest: any): boolean =>
    !isQrReminder || isQrReminderEligible(guest)

  const matchesCurrentFilters = useCallback((guest: any): boolean => (
    matchesInvitationFilter(guest, activeInv) &&
    matchesStatusWithFailureType(
      guest,
      statusField,
      failureTypeField,
      activeRem.id,
    ) &&
    (!isQrReminder || isQrReminderEligible(guest))
  ), [activeInv, activeRem.id, statusField, failureTypeField, isQrReminder])

  const filteredGuests = data_uploaded.filter(
    (g: any) => matchesInvFilter(g) && matchesRemFilter(g) && matchesQrEligibility(g),
  )

  const allSelected =
    filteredGuests.length > 0 &&
    filteredGuests.every((g: any) => selectedIds.has(g.id))

  const fetchAllFilteredGuests = useCallback(async () => {
    const allGuests = await fetchAllEventContacts(filterParams(), FETCH_ALL_PAGE_SIZE)
    return allGuests.filter(matchesCurrentFilters)
  }, [filterParams, matchesCurrentFilters])

  const clearSelection = () => {
    setSelectedIds(new Set())
    setSelectedGuestMap(new Map())
    setBulkGuests(null)
  }

  const handleSelectAll = async () => {
    if (allSelected) {
      clearSelection()
    } else if (!isQrReminder) {
      setSelectedIds(new Set(filteredGuests.map((g: any) => g.id)))
      setSelectedGuestMap(new Map(filteredGuests.map((g: any) => [g.id, g])))
      setBulkGuests(null)
    } else {
      setIsSelectingAll(true)
      try {
        const allGuests = await fetchAllFilteredGuests()
        setSelectedIds(new Set(allGuests.map((g: any) => g.id)))
        setSelectedGuestMap(new Map(allGuests.map((g: any) => [g.id, g])))
        setBulkGuests(null)
      } catch {
        notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
      } finally {
        setIsSelectingAll(false)
      }
    }
  }

  const handleSelectRow = (guest: any) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(guest.id)) {
        next.delete(guest.id)
      } else {
        next.add(guest.id)
      }
      return next
    })
    setSelectedGuestMap((prev) => {
      const next = new Map(prev)
      if (next.has(guest.id)) {
        next.delete(guest.id)
      } else {
        next.set(guest.id, guest)
      }
      return next
    })
    setBulkGuests(null)
  }

  const localFilter = {
    sort: "",
    order: "",
    searchValue,
    statusNumber: activeInv.statusNumber,
    waStatusNumber: activeInv.waStatusNumber,
    reminderNumber: "",
    id: Number(event_id),
  }

  const hasSelection = selectedIds.size > 0

  const selectedGuests = Array.from(selectedGuestMap.values()).filter((g: any) =>
    selectedIds.has(g.id),
  )
  const effectiveGuests = hasSelection ? selectedGuests : (bulkGuests ?? filteredGuests)

  const eligibleGuests = effectiveGuests.filter((g: any) => g.phone)

  const lang = i18n.language === "ar" ? "ar" : "en"
  const dateLocale = i18n.language?.toLowerCase().startsWith("ar") ? "ar-SA" : "en-US"

  const handleSendClick = async () => {
    if (hasSelection || !isQrReminder) {
      if (eligibleGuests.length === 0) {
        notify(t("eventDashboardV2.messages.noEligibleReminderGuests"), true)
        return
      }
      setEventDateError("")
      setSendPhase("confirm")
      return
    }

    setIsPreparingSend(true)
    try {
      const allGuests = await fetchAllFilteredGuests()
      const guestsWithPhone = allGuests.filter((g: any) => g.phone)
      if (guestsWithPhone.length === 0) {
        notify(t("eventDashboardV2.messages.noEligibleReminderGuests"), true)
        return
      }
      setBulkGuests(allGuests)
      setEventDateError("")
      setSendPhase("confirm")
    } catch {
      notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
    } finally {
      setIsPreparingSend(false)
    }
  }

  useEffect(() => {
    if (sendPhase === "confirm" && isQrReminder) {
      const focusTimer = window.setTimeout(() => dateInputRef.current?.focus(), 60)
      return () => clearTimeout(focusTimer)
    }
  }, [sendPhase, isQrReminder])

  useEffect(() => {
    setEventDateText("")
    setEventDateError("")
    setSendPhase("idle")
    clearSelection()
  }, [reminderType, invitationFilter, reminderFilter, searchValue])

  const handleSendConfirm = () => {
    const contactIds = eligibleGuests.map((g: any) => g.id)
    const skipped = effectiveGuests.length - contactIds.length

    if (isQrReminder) {
      const trimmed = eventDateText.trim()
      if (!trimmed) {
        setEventDateError(t("eventDashboardV2.messages.eventDateRequiredForMessage"))
        dateInputRef.current?.focus()
        return
      }
      setEventDateError("")
      setSendPhase("sending")
      dispatch(
        sendConfirmedQrReminders({
          event_id: Number(event_id),
          event_contact_ids: contactIds.join(","),
          event_date_text: trimmed,
        }),
      )
        .then((result: any) => {
          if (result.error) {
            notify(t("eventDashboardV2.messages.failedSendQrCode"), true)
            setSendPhase("confirm")
          } else {
            if (result.payload?.balance !== undefined) {
              dispatch(setBalance(result.payload.balance))
            }
            setSendResult({ sent: contactIds.length, skipped })
            setSendPhase("success")
            clearSelection()
            setEventDateText("")
            dispatch(setLoading())
            fetchGuests()
            setTimeout(() => setSendPhase("idle"), 4000)
          }
        })
        .catch(() => {
          notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
          setSendPhase("confirm")
        })
      return
    }

    setSendPhase("sending")
    dispatch(
      sendReminder({
        event_contact_ids: contactIds.join(","),
        event_id: Number(event_id),
        reminder_type: reminderType,
      }),
    )
      .then((result: any) => {
        if (result.error) {
          notify(t("eventDashboardV2.messages.failedSendReminders"), true)
          setSendPhase("idle")
        } else {
          if (result.payload?.balance !== undefined) {
            dispatch(setBalance(result.payload.balance))
          }
          setSendResult({ sent: contactIds.length, skipped })
          setSendPhase("success")
          clearSelection()
          dispatch(setLoading())
          fetchGuests()
          setTimeout(() => setSendPhase("idle"), 4000)
        }
      })
      .catch(() => {
        notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
        setSendPhase("idle")
      })
  }

  const handleSendCancel = () => {
    setSendPhase("idle")
    setEventDateError("")
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEventDateText(e.target.value)
    if (eventDateError) setEventDateError("")
  }

  const sendLabel =
    reminderType === 1
      ? t("eventDashboardV2.actions.sendRsvpReminder")
      : reminderType === 2
        ? t("eventDashboardV2.actions.sendQrCode")
        : t("eventDashboardV2.actions.sendReminder")

  const reminderBody =
    reminderType === 1
      ? (lang === "ar"
        ? event.reminder_accept_decline_template_ar_body_text
        : event.reminder_accept_decline_template_en_body_text)
      : (lang === "ar"
        ? event.reminder_remind_only_template_ar_body_text
        : event.reminder_remind_only_template_en_body_text)
  const locationText =
    reminderType === 1
      ? (lang === "ar"
        ? event.reminder_accept_decline_template_ar_location_button_text
        : event.reminder_accept_decline_template_en_location_button_text)
      : (lang === "ar"
        ? event.reminder_remind_only_template_ar_location_button_text
        : event.reminder_remind_only_template_en_location_button_text)
  const acceptText = lang === "ar"
    ? event.reminder_accept_decline_template_ar_accept_button_text
    : event.reminder_accept_decline_template_en_accept_button_text
  const declineText = lang === "ar"
    ? event.reminder_accept_decline_template_ar_decline_button_text
    : event.reminder_accept_decline_template_en_decline_button_text
  const renderPreviewReminderBody = () => {
    const body = reminderBody || t("eventDashboardV2.messages.reminderPreviewFallback", { event: event.name })
    return body.split(/(\{\{\s*[12]\s*\}\})/g).map((part: string, index: number) => {
      if (/\{\{\s*1\s*\}\}/.test(part)) {
        return (
          <em key={`guest-${index}`}>
            {t("eventDashboardV2.messages.previewGuestPlaceholder")}
          </em>
        )
      }
      if (/\{\{\s*2\s*\}\}/.test(part)) {
        return <React.Fragment key={`event-${index}`}>{event.name}</React.Fragment>
      }
      return part
    })
  }

  const remindersSentMessageKey =
    reminderType === 2
      ? "remindersSentQr"
      : reminderType === 1
        ? "remindersSentRsvp"
        : "remindersSentReminder"

  return (
    <div className={styles.guestListSection}>
      {sendPhase === "success" && (
        <div className={styles.sendSuccessBanner}>
          <CheckCircleIcon />
          <span>
            {t(`eventDashboardV2.messages.${remindersSentMessageKey}`, { count: sendResult.sent })}
            {sendResult.skipped > 0 && (
              <>
                {" "}
                &middot; {t("eventDashboardV2.messages.skipped", { count: sendResult.skipped })}
              </>
            )}
          </span>
        </div>
      )}

      <div className={styles.reminderPreviewPanel}>
        <div className={styles.reminderPreviewHeader}>
          <span className={styles.reminderPreviewEyebrow}>{t("eventDashboardV2.labels.preview")}</span>
          <strong>{sendLabel}</strong>
        </div>
        <div className={styles.reminderPreviewBubble}>
          <p>{renderPreviewReminderBody()}</p>
          <div className={styles.reminderPreviewActions}>
            {reminderType === 1 && (
              <>
                <span>{declineText || t("eventDashboardV2.actions.decline")}</span>
                <span>{acceptText || t("eventDashboardV2.actions.confirm")}</span>
              </>
            )}
            <span>{locationText || t("eventDashboardV2.actions.location")}</span>
          </div>
        </div>
      </div>

      <div className={styles.guestListHeader}>
        <div className={styles.searchInput}>
          <SearchIcon />
          <input
            type="text"
            placeholder={t("eventDashboardV2.actions.search")}
            value={searchValue}
            onChange={handleSearch}
          />
        </div>
        {!hasSelection && sendPhase !== "confirm" && sendPhase !== "sending" && (
          <div className={styles.guestListControls}>
            <div className={styles.filterSelectWrap}>
              <select
                className={styles.filterSelect}
                value={invitationFilter}
                onChange={handleInvFilterChange}
              >
                {INVITATION_FILTERS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.id === "all" ? t("eventDashboardV2.table.invitationStatus") : t(`eventDashboardV2.filters.${opt.id}`, opt.label)}
                  </option>
                ))}
              </select>
              <ChevronDownIcon />
            </div>
            <div className={styles.filterSelectWrap}>
              <select
                className={styles.filterSelect}
                value={reminderFilter}
                onChange={handleRemFilterChange}
              >
                {REMINDER_FILTERS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.id === "all" ? t("eventDashboardV2.table.reminderStatus") : t(`eventDashboardV2.filters.${opt.id}`, opt.label)}
                  </option>
                ))}
              </select>
              <ChevronDownIcon />
            </div>
            <button
              className={styles.sendReminderBtn}
              onClick={handleSendClick}
              disabled={isPreparingSend}
            >
              {isPreparingSend ? t("eventDashboardV2.actions.sending") : sendLabel}
            </button>
          </div>
        )}
      </div>

      {!hasSelection && (sendPhase === "confirm" || sendPhase === "sending") && (
        <div className={`${styles.sendConfirmBar} ${isQrReminder ? styles.sendConfirmBarStacked : ""}`}>
          {isQrReminder ? (
            <>
              <div className={styles.sendConfirmHeader}>
                <span className={styles.sendConfirmEyebrow}>{t("eventDashboardV2.messages.sendingQrCode")}</span>
                <span className={styles.sendConfirmContext}>
                  {t("eventDashboardV2.messages.qrSendToGuests", {
                    count: eligibleGuests.length,
                  })}
                  {effectiveGuests.length - eligibleGuests.length > 0 && (
                    <span className={styles.sendConfirmMuted}>
                      {" · "}
                      {t("eventDashboardV2.messages.noPhone", {
                        count: effectiveGuests.length - eligibleGuests.length,
                      })}
                    </span>
                  )}
                </span>
              </div>

              <div className={styles.sendConfirmBody}>
                <div className={styles.qrDateField}>
                  <label htmlFor="qr-event-date" className={styles.qrDateLabel}>
                    {t("eventDashboardV2.labels.eventDate")}
                    <span className={styles.qrDateRequired} aria-hidden="true">*</span>
                  </label>
                  <div
                    className={`${styles.qrDateInputWrap} ${
                      eventDateError ? styles.qrDateInputWrapError : ""
                    }`}
                  >
                    <CalendarIcon />
                    <input
                      ref={dateInputRef}
                      id="qr-event-date"
                      type="text"
                      className={styles.qrDateInput}
                      placeholder={t("eventDashboardV2.messages.eventDatePlaceholder")}
                      value={eventDateText}
                      onChange={handleDateChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && sendPhase !== "sending") {
                          handleSendConfirm()
                        }
                      }}
                      disabled={sendPhase === "sending"}
                      autoComplete="off"
                    />
                  </div>
                  {eventDateError ? (
                    <span className={styles.qrDateError}>{eventDateError}</span>
                  ) : (
                    <span className={styles.qrDateHelp}>
                      {t("eventDashboardV2.messages.eventDateHelp")}
                    </span>
                  )}
                </div>

                <div className={styles.sendConfirmActions}>
                  <button
                    type="button"
                    className={styles.sendConfirmCancel}
                    onClick={handleSendCancel}
                    disabled={sendPhase === "sending"}
                  >
                    {t("eventDashboardV2.actions.cancel")}
                  </button>
                  <button
                    type="button"
                    className={styles.sendConfirmBtn}
                    onClick={handleSendConfirm}
                    disabled={sendPhase === "sending" || !eventDateText.trim()}
                  >
                    {sendPhase === "sending" ? (
                      <><SpinnerIcon /> {t("eventDashboardV2.actions.sending")}</>
                    ) : (
                      t("eventDashboardV2.actions.sendQrCode")
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.sendConfirmInfo}>
                <span className={styles.sendConfirmText}>
                  {t("eventDashboardV2.messages.sendReminderTo", { label: sendLabel, count: eligibleGuests.length })}
                  {effectiveGuests.length - eligibleGuests.length > 0 && (
                    <> · {t("eventDashboardV2.messages.noPhone", { count: effectiveGuests.length - eligibleGuests.length })}</>
                  )}
                </span>
              </div>
              <div className={styles.sendConfirmActions}>
                <button
                  type="button"
                  className={styles.sendConfirmCancel}
                  onClick={handleSendCancel}
                  disabled={sendPhase === "sending"}
                >
                  {t("eventDashboardV2.actions.cancel")}
                </button>
                <button
                  type="button"
                  className={styles.sendConfirmBtn}
                  onClick={handleSendConfirm}
                  disabled={sendPhase === "sending"}
                >
                  {sendPhase === "sending" ? <><SpinnerIcon /> {t("eventDashboardV2.actions.sending")}</> : t("eventDashboardV2.actions.confirmAndSend")}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead className={styles.tableHead}>
          <tr>
            <th className={styles.checkboxCell}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allSelected}
                disabled={isSelectingAll}
                onChange={handleSelectAll}
              />
            </th>
            <th className={styles.colId}>{t("eventDashboardV2.table.id")}</th>
            <th className={styles.colName}>{t("eventDashboardV2.table.guestName")}</th>
            <th className={styles.colContact}>{t("eventDashboardV2.table.contact")}</th>
            <th className={styles.colStatus}>{t("eventDashboardV2.table.invitationStatus")}</th>
            <th className={styles.colStatus}>{t("eventDashboardV2.table.deliveryReport")}</th>
            <th className={styles.colActions}>{t("eventDashboardV2.table.actions")}</th>
          </tr>
        </thead>
        <tbody className={styles.tableBody}>
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <tr key={`skel-${i}`}>
                  <td className={styles.checkboxCell}>
                    <div className={styles.skeleton} style={{ width: 16, height: 16 }} />
                  </td>
                  <td><div className={styles.skeleton} style={{ width: 48 }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '70%' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '60%' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '50%' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '50%' }} /></td>
                  <td>
                    <div className={styles.actionsCell}>
                      <div className={styles.skeleton} style={{ width: 32, height: 32 }} />
                      <div className={styles.skeleton} style={{ width: 32, height: 32 }} />
                    </div>
                  </td>
                </tr>
              ))
            : filteredGuests.map((guest: any) => {
                const contact = getContactDisplay(guest)
                const invStatus = getUnifiedStatus(
                  guest.status,
                  guest.whatsapp_invitation_status ?? 0,
                  guest.invitation_failure_type,
                  event.is_open_event === 1,
                )
                const reminder = getReminderStatus(guest, reminderType, dateLocale)
                const reminderDot = getStatusDotColor(
                  reminder.variant === "Confirmed"
                    ? 1
                    : reminder.variant === "Issue"
                      ? 4
                      : reminder.variant === "InProgress"
                        ? 3
                        : 0,
                )
                const guestName = [guest.suffix, guest.first_name, guest.last_name]
                  .filter(Boolean)
                  .join(" ")

                return (
                  <tr key={guest.id}>
                    <td className={styles.checkboxCell}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={selectedIds.has(guest.id)}
                        onChange={() => handleSelectRow(guest)}
                      />
                    </td>
                    <td>{guest.id}</td>
                    <td className={styles.guestName}>{guestName}</td>
                    <td>
                      <div className={styles.contactCell}>
                        {contact.phone ? (
                          <>
                            <WhatsAppSmallIcon />
                            {contact.phone}
                          </>
                        ) : contact.email ? (
                          <>
                            <MailSmallIcon />
                            {contact.email}
                          </>
                        ) : (
                          "—"
                        )}
                        {contact.hasExtra && (
                          <span className={styles.contactBadge}>+1</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${styles[`statusBadge${invStatus.variant}`]}`}
                      >
                        <span
                          className={`${styles.statusDot} ${styles[`statusDot${invStatus.dotColor}`]}`}
                        />
                        {t(`eventDashboardV2.status.${invStatus.text}`, invStatus.text)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${styles[`statusBadge${reminder.variant}`]}`}
                      >
                        {reminder.text !== "—" && (
                          <span
                            className={`${styles.statusDot} ${styles[`statusDot${reminderDot}`]}`}
                          />
                        )}
                        {t(`eventDashboardV2.status.${reminder.text}`, reminder.text)}
                        {reminder.date && (
                          <span className={styles.statusDate}> &middot; {reminder.date}</span>
                        )}
                        {reminder.hasRetry && (
                          <span className={styles.retryLink}>{t("eventDashboardV2.actions.retry")}</span>
                        )}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionsCell}>
                        <button
                          className={styles.actionIconBtn}
                          title={t("eventDashboardV2.actions.edit")}
                          onClick={() => editPopupOpen(guest)}
                        >
                          <EditSmallIcon />
                        </button>
                        <DeleteButton
                          guestId={guest.id}
                          guestName={guestName}
                          eventId={String(event_id)}
                          filter={localFilter}
                          notify={notify}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
        </tbody>
      </table>
      </div>

      {filteredGuests.length === 0 && !isLoading && (
        <div className={styles.emptyState}>{t("eventDashboardV2.messages.noGuestsFound")}</div>
      )}

      {isFetchingMore && (
        <div className={styles.loadMoreRow}>
          <div className={styles.skeleton} style={{ width: "100%", height: 40 }} />
        </div>
      )}
      {hasMore && <div ref={sentinelRef} className={styles.scrollSentinel} />}

      {hasSelection && sendPhase !== "success" && (
        <div className={styles.selectionBar}>
          {sendPhase === "idle" && (
            <>
              <div className={styles.selectionBarLeft}>
                <span className={styles.selectionBadge}>{selectedIds.size}</span>
                <span className={styles.selectionLabel}>
                  {t("eventDashboardV2.messages.guestsSelected", { count: selectedIds.size })}
                </span>
                <button
                  type="button"
                  className={styles.selectionClear}
                  onClick={clearSelection}
                >
                  {t("eventDashboardV2.actions.clear")}
                </button>
              </div>
              <div className={styles.selectionBarRight}>
                <button
                  type="button"
                  className={styles.selectionSendBtn}
                  onClick={handleSendClick}
                  disabled={isPreparingSend}
                >
                  {isPreparingSend ? t("eventDashboardV2.actions.sending") : sendLabel}
                </button>
              </div>
            </>
          )}

          {(sendPhase === "confirm" || sendPhase === "sending") && (
            <div
              className={`${styles.selectionBarInner} ${
                isQrReminder ? styles.selectionBarInnerStacked : ""
              }`}
            >
              {isQrReminder ? (
                <>
                  <div className={styles.selectionBarHeader}>
                    <span className={styles.selectionBarEyebrow}>{t("eventDashboardV2.messages.sendingQrCode")}</span>
                    <span className={styles.selectionBarContext}>
                      {t("eventDashboardV2.messages.sendSelected", {
                        eligible: eligibleGuests.length,
                        selected: selectedIds.size,
                      })}
                      {selectedIds.size - eligibleGuests.length > 0 && (
                        <span className={styles.selectionBarMuted}>
                          {" · "}
                          {t("eventDashboardV2.messages.noPhone", { count: selectedIds.size - eligibleGuests.length })}
                        </span>
                      )}
                    </span>
                  </div>

                  <div className={styles.selectionBarBody}>
                    <div className={`${styles.qrDateField} ${styles.qrDateFieldOnDark}`}>
                      <label htmlFor="qr-event-date-selection" className={styles.qrDateLabel}>
                        {t("eventDashboardV2.labels.eventDate")}
                        <span className={styles.qrDateRequired} aria-hidden="true">*</span>
                      </label>
                      <div
                        className={`${styles.qrDateInputWrap} ${
                          eventDateError ? styles.qrDateInputWrapError : ""
                        }`}
                      >
                        <CalendarIcon />
                        <input
                          ref={dateInputRef}
                          id="qr-event-date-selection"
                          type="text"
                          className={styles.qrDateInput}
                          placeholder={t("eventDashboardV2.messages.eventDatePlaceholder")}
                          value={eventDateText}
                          onChange={handleDateChange}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && sendPhase !== "sending") {
                              handleSendConfirm()
                            }
                          }}
                          disabled={sendPhase === "sending"}
                          autoComplete="off"
                        />
                      </div>
                      {eventDateError ? (
                        <span className={styles.qrDateError}>{eventDateError}</span>
                      ) : (
                        <span className={styles.qrDateHelp}>
                          {t("eventDashboardV2.messages.eventDateHelp")}
                        </span>
                      )}
                    </div>

                    <div className={styles.selectionBarActions}>
                      <button
                        type="button"
                        className={styles.sendConfirmCancel}
                        onClick={handleSendCancel}
                        disabled={sendPhase === "sending"}
                      >
                        {t("eventDashboardV2.actions.cancel")}
                      </button>
                      <button
                        type="button"
                        className={styles.sendConfirmBtn}
                        onClick={handleSendConfirm}
                        disabled={sendPhase === "sending" || !eventDateText.trim()}
                      >
                        {sendPhase === "sending" ? (
                            <><SpinnerIcon /> {t("eventDashboardV2.actions.sending")}</>
                        ) : (
                            t("eventDashboardV2.actions.sendQrCode")
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.selectionBarLeft}>
                    <span className={styles.sendConfirmText}>
                      {t("eventDashboardV2.messages.sendSelected", { eligible: eligibleGuests.length, selected: selectedIds.size })}
                    </span>
                    {selectedIds.size - eligibleGuests.length > 0 && (
                      <span className={styles.sendSkippedText}>
                        {t("eventDashboardV2.messages.noPhone", { count: selectedIds.size - eligibleGuests.length })}
                      </span>
                    )}
                  </div>
                  <div className={styles.selectionBarRight}>
                    <button
                      type="button"
                      className={styles.sendConfirmCancel}
                      onClick={handleSendCancel}
                      disabled={sendPhase === "sending"}
                    >
                      {t("eventDashboardV2.actions.cancel")}
                    </button>
                    <button
                      type="button"
                      className={styles.sendConfirmBtn}
                      onClick={handleSendConfirm}
                      disabled={sendPhase === "sending"}
                    >
                      {sendPhase === "sending" ? (
                        <><SpinnerIcon /> {t("eventDashboardV2.actions.sending")}</>
                      ) : (
                        t("eventDashboardV2.actions.confirmAndSend")
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default RemindersGuestList
